"use client";

import type { AlgoliaProductCard, ProductDetails } from "@raumagarn/formatters";
import { useSelection } from "@trydig/centra/client";
import type { PaymentResult } from "@trydig/centra-types/orders";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";
import { gtm } from "~/lib/gtm";
import { getUserData } from "~/lib/gtm/hooks";
import {
	productCardsToGTMProducts,
	productDetailsToGTMProduct,
	selectionItemsToGTMProducts,
} from "~/lib/gtm/utils";
import { useLocation } from "~/lib/hooks/useCentra";
import { usePrice } from "~/lib/hooks/usePrice";
import { getBaseUrl } from "~/lib/utils/getBaseUrl";

export const GTMViewItem = ({ product }: { product: ProductDetails }) => {
	const hasRan = useRef<boolean>(false);
	const price = usePrice({ pricelist: product.pricelist });
	const { data: selection } = useSelection();

	useEffect(() => {
		if (!selection?.selection?.currency || hasRan.current) return;

		const item = productDetailsToGTMProduct({
			product,
			price,
			currency: selection.selection.currency,
		});

		gtm.viewItem(item);

		hasRan.current = true;
	}, [product, selection?.selection?.currency, price]);

	return null;
};

export const GTMViewItemList = ({
	products,
	categoryPage,
	categoryId,
}: {
	products: AlgoliaProductCard[];
	categoryPage: string;
	categoryId: string;
}) => {
	const hasRan = useRef<boolean>(false);
	const { data: selection } = useSelection();

	useEffect(() => {
		if (!products || !selection?.selection?.currency) return;

		const items = productCardsToGTMProducts({
			products,
			currency: selection.selection.currency,
			categoryPage,
		});

		gtm.viewItemList({
			items,
			currency: selection.selection.currency,
			item_list_id: categoryId,
			item_list_name: categoryPage,
		});

		hasRan.current = true;
	}, [categoryId, categoryPage, products, selection?.selection?.currency]);
	return null;
};

export const GTMBeginCheckout = () => {
	const { data: selection } = useSelection();

	useEffect(() => {
		if (
			!selection?.selection?.items ||
			selection?.selection?.items?.length === 0 ||
			!selection?.selection?.currency
		)
			return;

		const items = selectionItemsToGTMProducts({
			items: selection.selection.items,
			currency: selection.selection.currency,
		});

		if (items.length > 0) {
			gtm.beginCheckout({
				items,
				currency: selection.selection.currency,
				value: items?.reduce((a, b) => (a + b.price) * (b.quantity || 1), 0),
			});
		}
	}, [selection?.selection?.items, selection?.selection?.currency]);

	return null;
};

export const GTMAddShipmentMethod = () => {
	const { data: selection } = useSelection();

	useEffect(() => {
		if (
			!selection?.selection?.shippingMethodName ||
			!selection.selection.currency ||
			!selection?.selection?.items
		)
			return;

		const items = selectionItemsToGTMProducts({
			items: selection.selection.items,
			currency: selection.selection.currency,
		});
		gtm.addShippingMethod({
			shipping_method: selection.selection.shippingMethodName,
			currency: selection.selection.currency,
			items,
			value: items?.reduce((a, b) => (a + b.price) * (b.quantity || 1), 0),
		});
	}, [selection]);

	return null;
};

export const GTMAddPaymentInfo = () => {
	const { data: selection } = useSelection();
	const hasRan = useRef(false);

	useEffect(() => {
		if (
			hasRan.current ||
			!selection?.selection?.items ||
			selection?.selection?.items?.length === 0 ||
			!selection?.selection?.currency
		)
			return;

		const items = selectionItemsToGTMProducts({
			items: selection.selection.items,
			currency: selection.selection.currency,
		});

		gtm.addPaymentInfo({
			payment_method: "Adyen Drop-In",
			items,
			currency: selection.selection.currency,
			value: items?.reduce((a, b) => (a + b.price) * (b.quantity || 1), 0),
		});

		hasRan.current = true;
	}, [selection?.selection?.items, selection?.selection?.currency]);

	return null;
};

export const GTMPurchase = ({ order }: { order: PaymentResult["order"] }) => {
	const hasRan = useRef(false);

	useEffect(() => {
		if (!order || hasRan.current || !order.currency || !order.items) return;

		gtm.purchase({
			coupon: order.discounts?.vouchers?.map((v) => v.voucher)?.join(";"),
			currency: order.currency,
			items: selectionItemsToGTMProducts({
				items: order.items,
				currency: order.currency,
			}),
			transaction_id: order.order as string,
			tax: order.totals.grandTotalPriceTaxAsNumber || 0,
			value: order.totals.grandTotalPriceAsNumber || 0,
			shipping_tier: order.shippingMethodName as string,
			shipping: order.totals.shippingPriceAsNumber || 0,
			payment_type: order.paymentMethodName as string,
			value_ink_vat: order.totals.grandTotalPriceAsNumber || 0,
			value_ex_vat:
				(order.totals.grandTotalPriceAsNumber || 0) -
				(order.totals.grandTotalPriceTaxAsNumber || 0),
		});

		hasRan.current = true;
	}, [order]);

	return null;
};

export const PageView = () => {
	const { data: location } = useLocation();
	const pathname = usePathname();
	const url = getBaseUrl();
	const isTriggering = useRef(false);
	const isInitialLoad = useRef(true);

	const getPageType = useCallback(() => {
		if (pathname === "/") {
			return "home_page";
		}
		if (pathname.includes("/produkt/")) {
			return "product_detail_page";
		}
		if (pathname.includes("/produkter/")) {
			return "“product_listing_page”";
		}
		if (pathname.includes("checkout") && pathname.includes("summary")) {
			return "order_confirmation";
		}
		if (pathname.includes("checkout")) {
			return "checkout";
		}
		if (pathname.includes("/login")) {
			return "login";
		}
		if (pathname.includes("/konto")) {
			return "account";
		}

		return "other";
	}, [pathname]);

	useEffect(() => {
		if (isTriggering.current || !location?.name) {
			return;
		}

		isTriggering.current = true;

		const triggerType = isInitialLoad.current ? "hard" : "soft";
		isInitialLoad.current = false;

		gtm.pageView({
			event: "page_view",
			page_path: pathname,
			page_info: {
				url: typeof window !== "undefined" ? window.location.href : `${url}${pathname}`,
				virtal_url: `${url}${pathname}`,
				page_path: `${pathname}`,
				page_title: typeof window !== "undefined" ? document.title : "",
				page_type: getPageType(),
				page_referrer: typeof window !== "undefined" ? document.referrer : "",
				trigger_type: triggerType,
				market: location?.name,
				locale: "nb",
			},
			user_data: getUserData(),
		});

		isTriggering.current = false;
	}, [getPageType, location?.name, pathname, url]);

	return null;
};
